import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Check, Crosshair, Map as MapIcon, Satellite, Search, X } from 'lucide-react';
import { formatNumber } from '@/components/live';
import { toast } from '@/lib/toast';

export interface HousePoint { lat: number; lng: number; accuracy: number | null }

const STREET_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const STREET_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTRIBUTION = 'Imagery &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community';
const LABELS_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}';
const INDIA: [number, number] = [22.9734, 78.6569];

/** A plain dot rather than Leaflet's default pin image, which a bundler does not ship. */
const HOUSE_ICON = L.divIcon({ className: 'house-pin', html: '<span></span>', iconSize: [22, 22], iconAnchor: [11, 11] });

interface Place { label: string; lat: number; lng: number; kind: string }

const GEOCODE_URL = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

/**
 * Find a place by name (owner, 2026-09-21: "add option to search on map"). Esri's World Geocoder,
 * free without a key because nothing is stored - the result only moves the map. Also accepts
 * coordinates typed or pasted as "22.9685, 78.6621".
 */
export async function findPlaces(query: string): Promise<Place[]> {
  const coords = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(query);
  if (coords) return [{ label: `${coords[1]}, ${coords[2]}`, lat: Number(coords[1]), lng: Number(coords[2]), kind: 'Coordinates' }];
  const params = new URLSearchParams({ f: 'json', singleLine: query, countryCode: 'IND', maxLocations: '6', outFields: 'Addr_type' });
  const response = await fetch(`${GEOCODE_URL}?${params.toString()}`);
  if (!response.ok) throw new Error('Place search is unavailable right now.');
  const data = await response.json() as { candidates?: Array<{ address: string; location: { x: number; y: number }; attributes?: { Addr_type?: string } }> };
  return (data.candidates ?? []).map((c) => ({ label: c.address, lat: c.location.y, lng: c.location.x, kind: c.attributes?.Addr_type ?? '' }));
}

/**
 * Pick the farmer's house on a map (owner, 2026-09-21: "GPS of the farmer's house with map
 * selection").
 *
 * Two ways in, because both happen in the field: standing at the house, "Use my location" reads
 * the device GPS (and keeps its accuracy); from the office, tap the house on the satellite
 * image, or search for the village or a landmark first. The pin can be dragged to correct any of
 * them, and a new pin is confirmed before it counts. A point chosen on the map carries no
 * accuracy figure - it is as good as the eye that placed it, and the record says so.
 */
export function HouseLocationPicker({
  value,
  onChange,
  placeHint,
  subject = 'house',
  owner = 'farmer',
}: {
  /** What is being located, in the messages ("house", "site"), and whose record it is saved with. */
  subject?: string;
  owner?: string;
  value: HousePoint | null;
  onChange: (point: HousePoint | null) => void;
  /** "Village, District, State" - the map opens there when no house point is set yet. */
  placeHint?: string | null;
}) {
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const layers = useRef<{ satellite: L.Layer; map: L.Layer } | null>(null);
  const change = useRef(onChange);
  change.current = onChange;
  const setConfirmedRef = useRef<(value: boolean) => void>(() => undefined);
  const [base, setBase] = useState<'satellite' | 'map'>('satellite');
  const [locating, setLocating] = useState(false);
  /** What the browser says about location, so the button can be honest before it is pressed. */
  const [permission, setPermission] = useState<'granted' | 'prompt' | 'denied' | 'unknown'>('unknown');
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState<Place[] | null>(null);
  const [searching, setSearching] = useState(false);
  // A pin placed or moved is "unconfirmed" until the officer confirms it is the house.
  const [confirmed, setConfirmed] = useState(Boolean(value));
  const centredFor = useRef<string | null>(null);
  setConfirmedRef.current = setConfirmed;

  function setPoint(point: HousePoint | null) {
    setConfirmed(false);
    change.current(point);
  }

  async function search() {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const found = await findPlaces(query.trim());
      setPlaces(found);
      if (found.length === 0) toast.error('Nothing found. Try the village and district, or paste coordinates.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Place search failed.');
    } finally {
      setSearching(false);
    }
  }

  function goTo(place: Place) {
    setPlaces(null);
    setQuery(place.label);
    map.current?.setView([place.lat, place.lng], place.kind === 'Coordinates' ? 18 : 16);
    setPoint({ lat: round(place.lat), lng: round(place.lng), accuracy: null });
  }

  function confirm() {
    if (!value) return;
    setConfirmed(true);
    toast.success(`${subject.charAt(0).toUpperCase()}${subject.slice(1)} location confirmed at ${formatNumber(value.lat, 6)}, ${formatNumber(value.lng, 6)}. It is saved with the ${owner}.`);
  }


  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, {
      center: value ? [value.lat, value.lng] : INDIA,
      zoom: value ? 17 : 5,
    });
    const street = L.tileLayer(STREET_URL, { attribution: STREET_ATTRIBUTION, maxZoom: 19 });
    const satellite = L.layerGroup([
      L.tileLayer(SATELLITE_URL, { attribution: SATELLITE_ATTRIBUTION, maxNativeZoom: 18, maxZoom: 20 }),
      L.tileLayer(LABELS_URL, { maxNativeZoom: 18, maxZoom: 20, opacity: 0.9 }),
    ]);
    satellite.addTo(instance);
    layers.current = { satellite, map: street };

    instance.on('click', (event: L.LeafletMouseEvent) => {
      setConfirmedRef.current(false);
      change.current({ lat: round(event.latlng.lat), lng: round(event.latlng.lng), accuracy: null });
    });

    map.current = instance;
    setTimeout(() => instance.invalidateSize(), 150);
    return () => { instance.remove(); map.current = null; marker.current = null; layers.current = null; };
    // The map is created once; `value` moves the marker in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Where the map opens on a NEW record (owner, 2026-09-25: "address the issue of getting pointed
   * to current location as initial value").
   *
   * It CENTRES on the device, it does not set the house point. The officer may be in the office,
   * and a house location filled in from wherever the laptop happens to be is a wrong fact that
   * looks like a recorded one. The pin is still tapped, dragged and confirmed by hand.
   *
   * And it only reads the device when permission is ALREADY granted. Asking on open puts a
   * browser prompt in front of a form nobody has started filling in, which is how a permission
   * gets refused for good - which is exactly what the screenshot showed.
   */
  useEffect(() => {
    if (value) return;
    let cancelled = false;
    const centre = () => navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled || !map.current) return;
        // Only if nothing else has moved the map in the meantime.
        if (centredFor.current === null) map.current.setView([position.coords.latitude, position.coords.longitude], 17);
      },
      () => undefined,
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );

    if (!('geolocation' in navigator)) { setPermission('denied'); return; }
    if (!navigator.permissions?.query) { setPermission('prompt'); return; }
    navigator.permissions.query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (cancelled) return;
        setPermission(status.state as 'granted' | 'prompt' | 'denied');
        status.onchange = () => setPermission(status.state as 'granted' | 'prompt' | 'denied');
        if (status.state === 'granted') centre();
      })
      .catch(() => { if (!cancelled) setPermission('prompt'); });
    return () => { cancelled = true; };
    // Once, for a new record; `value` arriving later means the officer has placed a pin.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open on the farmer's village when no house point exists yet, instead of all of India.
  useEffect(() => {
    if (value || !placeHint || centredFor.current === placeHint || !map.current) return;
    centredFor.current = placeHint;
    findPlaces(placeHint)
      .then((found) => { if (found[0] && !value) map.current?.setView([found[0].lat, found[0].lng], 15); })
      .catch(() => undefined);
  }, [placeHint, value]);

  // Keep the pin where the value says.
  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    if (!value) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    if (!marker.current) {
      marker.current = L.marker([value.lat, value.lng], { draggable: true, icon: HOUSE_ICON }).addTo(instance);
      marker.current.on('dragend', () => {
        const at = marker.current!.getLatLng();
        setConfirmedRef.current(false);
        change.current({ lat: round(at.lat), lng: round(at.lng), accuracy: null });
      });
    } else {
      marker.current.setLatLng([value.lat, value.lng]);
    }
  }, [value]);

  function switchBase(next: 'satellite' | 'map') {
    const instance = map.current;
    if (!instance || !layers.current || next === base) return;
    instance.removeLayer(layers.current[base]);
    layers.current[next].addTo(instance);
    setBase(next);
  }

  function useMyLocation() {
    if (!('geolocation' in navigator)) {
      toast.error('This device cannot read its location.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        const point = {
          lat: round(position.coords.latitude),
          lng: round(position.coords.longitude),
          accuracy: Math.round(position.coords.accuracy),
        };
        setPoint(point);
        map.current?.setView([point.lat, point.lng], 18);
      },
      (error) => {
        setLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          // Remembered, so the button stops offering something the browser will refuse again.
          setPermission('denied');
          toast.error(`Location is blocked for this site in the browser. Allow it in the address bar, or tap the ${subject} on the map.`);
          return;
        }
        toast.error(`The location could not be read. Wait a moment for GPS, or tap the ${subject} on the map.`);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }

  return (
    <div className="house-picker">
      <div className="plot-map-tools">
        <button className="primary" disabled={locating || permission === 'denied'} onClick={useMyLocation}
                title={permission === 'denied'
                  ? 'Location is blocked for this site in the browser. Allow it in the address bar to use this.'
                  : 'Reads the device GPS and keeps its accuracy'}
                type="button">
          <Crosshair size={15} /> {locating ? 'Reading GPS...' : 'Use my location'}
        </button>
        <button onClick={() => switchBase(base === 'satellite' ? 'map' : 'satellite')} type="button">
          {base === 'satellite' ? <><MapIcon size={15} /> Map</> : <><Satellite size={15} /> Satellite</>}
        </button>
        {value && !confirmed && (
          <button className="primary" onClick={confirm} type="button"><Check size={15} /> Confirm this location</button>
        )}
        {value && (
          <button onClick={() => setPoint(null)} type="button"><X size={15} /> Clear</button>
        )}
      </div>
      <div className="map-search">
        <Search size={15} />
        <input onChange={(event) => setQuery(event.target.value)}
               onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void search(); } }}
               placeholder="Search a village, landmark or address - or paste 22.97, 78.66" value={query} />
        <button disabled={searching || !query.trim()} onClick={() => void search()} type="button">{searching ? 'Searching...' : 'Search'}</button>
        {places && places.length > 0 && (
          <ul className="map-search-results">
            {places.map((place) => (
              <li key={`${place.lat},${place.lng}`}>
                <button onClick={() => goTo(place)} type="button">
                  <strong>{place.label}</strong>{place.kind && <small>{place.kind}</small>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className={`plot-map-hint${value && confirmed ? ' confirmed' : ''}`}>
        {value
          ? <>{confirmed ? 'Confirmed: ' : 'Pin at '}<strong>{formatNumber(value.lat, 6)}, {formatNumber(value.lng, 6)}</strong>
              {value.accuracy != null ? ` (GPS, ± ${value.accuracy} m)` : ' (placed on the map)'}.
              {confirmed ? ' Drag the pin or tap the map to change it.' : ` Drag the yellow pin onto the ${subject}, then confirm.`}</>
          : `Standing at the ${subject}? Use my location. Otherwise search for the village, zoom in, and tap the ${subject}.`}
      </p>
      <div className="house-picker-canvas" ref={container} />
    </div>
  );
}

/** Six decimals is about 10 cm - more is false precision from a phone. */
function round(value: number): number {
  return Math.round(value * 1e6) / 1e6;
}
